import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'

import {
  FormattedMessage,
  injectIntl,
} from 'react-intl'

import {
  inviteUser,
} from '../../../actions/user'

import {
  CapitalButton,
} from '../Navigation/Buttons'

import {
  EmailInput,
  SelectInput,
  isNumber,
  isEmail,
} from '../Inputs/Inputs'

import AppStore from '../../../stores/AppStore'

import styles from '../../../../css/home.module.css'

const ENGLISH_LANGUAGE = 'en'
const SPANISH_LANGUAGE = 'es'
const FRENCH_LANGUAGE = 'fr'

function Invite ({ intl: { formatMessage, locale }, user: { groups } }) {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [groupsState, setGroupsState] = useState(groups || '')
  const [language, setLanguage] = useState(locale)
  const [level, setLevel] = useState(0)
  const [invalid, setInvalid] = useState(true)
  const [saved, setSaved] = useState(false)

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [])

  const validate = useCallback((email, level) => {
    const newInvalid = !(isEmail(email) && (level === 20 || level === 10 || level === 0))

    setInvalid(newInvalid)

    if (saved === 'error') {
      setSaved(false)
    }
  }, [saved])

  const handleChange = useCallback((field, value) => {
    let newValue = value
    if (value !== '' && isNumber(value)) {
      newValue = Number(value)
    }

    // Update the appropriate state based on field
    if (field === 'email') setEmail(newValue)
    if (field === 'groups') setGroupsState(newValue)
    if (field === 'language') setLanguage(newValue)
    if (field === 'level') setLevel(newValue)

  }, [email, level])

  useEffect(() => {
    validate(email, level)
  }, [email, level, validate])

  const saveButtonFunction = useCallback(() => {
    const customer_id = AppStore.getCustomer().id
    const brand_name = AppStore.getCustomerBrand().name
    const groupIDs = AppStore.getUser().groups.map((group) => group.id)

    inviteUser(
      {
        customer_id,
        email,
        brand_name,
        permissions: { level },
        language,
        groupIDs,
      },
      (error) => {
        if (error) {
          setSaved('error')
        } else {
          setSaved(true)
          setTimeout(() => {
            backToUsers()
          }, 500)
        }
      }
    )
  }, [email, language, level])

  const backToUsers = useCallback(() => {
    const { id: customerID } = AppStore.getCustomer()
    navigate(`/${customerID}/settings/users`)
  }, [navigate])

  let saveButtonStyle = `${styles.saveButton} saveButton`

  if (invalid) {
    saveButtonStyle = `${styles.saveButton} saveButton`
  } else if (saved === true) {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonSuccess}`
  } else if (saved === 'error') {
    saveButtonStyle = `${styles.saveButton} saveButton ${styles.saveButtonError}`
  }

  const levelContext = AppStore.getUser()?.permissions?.level || 0

  const userLevelOptions = [
    {
      value: 0,
      name: formatMessage({
        id: 'invite.accountType.viewer',
        defaultMessage: 'Viewer',
        description: '',
      }),
    },
    {
      value: 10,
      name: formatMessage({
        id: 'invite.accountType.manager',
        defaultMessage: 'Manager',
        description: '',
      }),
    },
  ]

  if (levelContext >= 20) {
    userLevelOptions.push({
      value: 20,
      name: formatMessage({
        id: 'invite.accountType.admin',
        defaultMessage: 'Administrator',
        description: '',
      }),
    })
  }

  return (
    <div>
      <div className={styles.userInviteInput}>
        <section className={styles.userInput}>
          <FormattedMessage
            id='invite.email'
            defaultMessage='Email'
            description='Email label'
            tagName='h5'
          />
          <div className={styles.userInputValueEdit}>
            <EmailInput
              value={email}
              childChange={childChange}
              field='email'
            />
          </div>
        </section>
        <section>
          <FormattedMessage
            id='profile.language.name'
            defaultMessage='Language'
            description='Language label'
            tagName='h5'
          />
          <div className={styles.userInputValueEdit}>
            <SelectInput
              value={language}
              field='language'
              childChange={childChange}
              options={[
                {
                  value: ENGLISH_LANGUAGE,
                  selected: (language === ENGLISH_LANGUAGE),
                  name: formatMessage({
                    id: 'profile.language.english',
                    defaultMessage: 'English',
                    description: 'English language selection',
                  }),
                },
                {
                  value: SPANISH_LANGUAGE,
                  selected: (language === SPANISH_LANGUAGE),
                  name: formatMessage({
                    id: 'profile.language.spanish',
                    defaultMessage: 'Spanish',
                    description: 'Spanish language selection',
                  }),
                },
                {
                  value: FRENCH_LANGUAGE,
                  selected: (language === FRENCH_LANGUAGE),
                  name: formatMessage({
                    id: 'profile.language.french',
                    defaultMessage: 'French',
                    description: 'French language selection',
                  }),
                },
              ]}
              style={styles.selectInput}
            />
          </div>
        </section>
        {levelContext >= 10
          ? (
            <section className={styles.userInput}>
              <FormattedMessage
                id='invite.accountType.name'
                defaultMessage='Account Type'
                description='Account Type label'
                tagName='h5'
              />
              <div className={styles.userInputValueEdit}>
                <SelectInput
                  value={level}
                  field='level'
                  childChange={childChange}
                  options={userLevelOptions}
                  style={styles.selectInput}
                />
              </div>
            </section>
            )
          : ''}
      </div>

      <CapitalButton
        text={formatMessage({
          id: 'invite.send',
          defaultMessage: 'SEND INVITE',
          description: '',
        })}
        buttonFunction={saveButtonFunction}
        hSize='h2'
        style={saveButtonStyle}
        disabled={invalid}
      />
    </div>
  )
}

Invite.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    locale: PropTypes.string,
  }),
  user: PropTypes.shape({
    groups: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
}

export default injectIntl(Invite)
